/**
 * wall-art-gallery-dots.js
 *
 * Mobile-only pagination dots under the product gallery carousel. Implies "there
 * are more images" on the swipe carousel (thumbnails are hidden on mobile).
 *
 * Builds one dot per VISIBLE slide — slots hidden via `data-no-video` /
 * `data-no-image` (the per-format video/image slots) are skipped — marks the
 * active dot as the customer swipes (computed from scrollLeft, independent of
 * Dawn's is-active class), and lets a tap jump to a slide. Rebuilds whenever the
 * visible slide set changes (format swap toggles data-no-*, or the wall-art
 * renderer injects composites) via a MutationObserver.
 *
 * The dots container `.wall-art-gallery-dots` is rendered by
 * snippets/product-media-gallery.liquid and is CSS-hidden above 749px, so this
 * controller is effectively a no-op on desktop.
 */
(function () {
  'use strict';

  const controllers = new Map();

  function visibleSlides(slider) {
    return Array.from(slider.children).filter((li) => {
      if (!li.classList || !li.classList.contains('slider__slide')) return false;
      if (li.hasAttribute('data-no-video') || li.hasAttribute('data-no-image') || li.hasAttribute('data-no-featured')) return false;
      // Skip slides Dawn (or anything else) has display:none'd.
      return li.offsetParent !== null || li.getClientRects().length > 0;
    });
  }

  function activeIndexFor(slider, slides) {
    if (slides.length === 0) return 0;
    const center = slider.scrollLeft + slider.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < slides.length; i++) {
      const li = slides[i];
      const c = li.offsetLeft + li.offsetWidth / 2;
      const d = Math.abs(c - center);
      if (d < bestDist) { bestDist = d; best = i; }
    }
    return best;
  }

  function initForGallery(gallery) {
    const sectionId = String(gallery.id || '').replace(/^MediaGallery-/, '');
    if (!sectionId) return;

    const slider = gallery.querySelector('[id^="Slider-Gallery-"]');
    const dots = gallery.querySelector('.wall-art-gallery-dots');
    if (!slider || !dots) return;

    if (controllers.has(sectionId)) {
      try { controllers.get(sectionId).abort(); } catch { /* ignore */ }
      controllers.delete(sectionId);
    }
    const ac = new AbortController();
    controllers.set(sectionId, ac);

    let slides = [];
    let scrollRaf = 0;
    let mutRaf = 0;

    function syncActive() {
      const idx = activeIndexFor(slider, slides);
      const children = dots.children;
      for (let i = 0; i < children.length; i++) {
        children[i].classList.toggle('is-active', i === idx);
      }
    }

    function onScroll() {
      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(() => { scrollRaf = 0; syncActive(); });
    }

    function rebuild() {
      slides = visibleSlides(slider);
      dots.textContent = '';
      // One slide (or none) → no dots to show.
      if (slides.length < 2) return;
      slides.forEach((li, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'wall-art-gallery-dots__dot';
        b.setAttribute('aria-label', 'Go to image ' + (i + 1));
        b.addEventListener(
          'click',
          () => { li.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' }); },
          { signal: ac.signal },
        );
        dots.appendChild(b);
      });
      syncActive();
    }

    slider.addEventListener('scroll', onScroll, { passive: true, signal: ac.signal });
    window.addEventListener('resize', onScroll, { passive: true, signal: ac.signal });

    // Rebuild when the visible slide set changes: format swap toggles
    // data-no-video / data-no-image on the slot <li>s, and the wall-art renderer
    // adds/replaces composite nodes. Coalesced to one rebuild per frame.
    const mo = new MutationObserver(() => {
      if (mutRaf) return;
      mutRaf = requestAnimationFrame(() => { mutRaf = 0; rebuild(); });
    });
    mo.observe(slider, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-no-video', 'data-no-image', 'data-no-featured'],
    });
    ac.signal.addEventListener('abort', () => mo.disconnect());

    rebuild();
  }

  function initAll(root) {
    const scope = (root && root.querySelectorAll) ? root : document;
    if (root && root.matches && root.matches('media-gallery')) initForGallery(root);
    scope.querySelectorAll('media-gallery').forEach((g) => initForGallery(g));
  }

  function destroySection(sectionId) {
    const ctrl = controllers.get(sectionId);
    if (!ctrl) return;
    try { ctrl.abort(); } catch { /* ignore */ }
    controllers.delete(sectionId);
  }

  function start() {
    initAll(document);

    document.addEventListener('shopify:section:load', (event) => {
      const target = event && event.target instanceof Element ? event.target : document;
      initAll(target);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const detailSectionId = String((event && event.detail && event.detail.sectionId) || '').trim();
      if (detailSectionId) { destroySection(detailSectionId); return; }
      const target = event && event.target instanceof Element ? event.target : null;
      if (!target) return;
      const gallery = (target.matches && target.matches('media-gallery'))
        ? target
        : (target.querySelector && target.querySelector('media-gallery'));
      if (!gallery) return;
      const derived = String(gallery.id || '').replace(/^MediaGallery-/, '');
      if (derived) destroySection(derived);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
